import type { ProjectedCopy } from '../projectedCopy';
import type { ServerDocsCopyStore } from '../useDataSource';

/**
 * Where a signed-in STAFF user's phone or laptop keeps its copy of a projected list (today: the generators catalogue) between page loads, so the
 * next load asks the server only for what changed (~3 KB) instead of the whole list (~290 KB). Without it the main app starts every page load with
 * no copy (OFFICEHUB-CLIENT-9C: iPhone Safari, 10-36 s to download the full list). The technician app keeps the same kind of copy in its own store
 * (client-tech/src/offline); this module is the reusable version for any app.
 *
 * The copy is only ever a BASE for a request: the app still asks the server on every read, and useDataSource discards a copy that does not add up.
 * Customer data is at rest on the device, so every guard the technician store has is here too:
 *   - only allow-listed sources are written (a future projected list is NOT persisted until it is listed on purpose);
 *   - every entry is scoped to the signed-in uid and is neither served nor kept for anyone else;
 *   - an entry is dropped when older than 7 days or written by another build;
 *   - a write that finishes after the user changed is undone;
 *   - wipeAll (sign-out / user switch, via clearOfflineCache) and wipeOthers (a new staff user registers) delete by prefix.
 * Storage is advisory: nothing here throws into a read.
 */
export const SERVER_DOCS_COPY_KEY_PREFIX = 'oh-server-docs-copy:v1:';
/** Only these sources are kept on the device: each saved list is customer data at rest (~1 MB). */
export const SERVER_DOCS_PERSISTED_SOURCES: readonly string[] = ['generators'];
export const SERVER_DOCS_COPY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Write at most this often per list: each write clones the whole list (~1 MB), and a copy a few minutes old only makes the next delta slightly larger. */
export const SERVER_DOCS_COPY_WRITE_EVERY_MS = 10 * 60 * 1000;

/** The few storage operations needed; IndexedDB in the app, a Map in tests. */
export interface CopyBackend {
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
  del(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

interface StoredEntry {
  readonly v: 1;
  readonly uid: string;
  readonly shape: string;
  readonly savedAt: number;
  readonly copy: ProjectedCopy;
}

export interface PersistentServerDocsCopyStore extends ServerDocsCopyStore {
  /** Delete every copy this store wrote, for every user. Never throws. */
  wipeAll(): Promise<void>;
  /** Delete every copy that is not `uid`'s. Never throws. */
  wipeOthers(uid: string): Promise<void>;
  /** Stop this store for good (the registrar's cleanup): later loads find nothing, later saves write nothing, and a write still in flight is taken back. */
  dispose(): void;
}

export interface PersistentCopyStoreOptions {
  readonly backend: CopyBackend;
  /** The signed-in user's uid, '' when nobody is. Read at every step, never cached. */
  readonly getUid: () => string;
  /** The app build; a copy saved by another build is never used (the row shape could differ). */
  readonly buster: string;
  readonly now?: () => number;
  /** Told ONCE per store about a failed write (full disk, IndexedDB error). */
  readonly onError?: (error: unknown) => void;
  /** Told ONCE per store when a wipe could not delete after a retry: the previous user's customer data may still be on the device. Defaults to onError. */
  readonly onWipeFailure?: (error: unknown) => void;
}

const isQuotaError = (error: unknown): boolean => error instanceof Error && /quota/i.test(`${error.name} ${error.message}`);
const isPersisted = (key: string): boolean => SERVER_DOCS_PERSISTED_SOURCES.includes(key.split('|')[0]);

const isCopy = (v: unknown): v is ProjectedCopy => {
  const c = v as Partial<ProjectedCopy> | null;
  return !!c && Array.isArray(c.columns) && Array.isArray(c.rows) && c.rows.length > 0 && typeof c.asOf === 'string'
    && typeof c.idsHash === 'string' && typeof c.count === 'number' && typeof c.columnsHash === 'string';
};

export function createPersistentServerDocsCopyStore(opts: PersistentCopyStoreOptions): PersistentServerDocsCopyStore {
  const { backend, getUid, buster, onError } = opts;
  const now = opts.now ?? Date.now;
  const shape = `v1|${buster}`;
  const entryKey = (uid: string, key: string): string => `${SERVER_DOCS_COPY_KEY_PREFIX}${uid}:${key}`;
  const lastWrite = new Map<string, number>();
  let storageGivenUp = false; // a full disk stops further writes for the session
  let disposed = false;
  let wipeFailed = false; // a wipe that could not complete: keep no more customer data on this device this session (fail closed)
  let reported = false;
  const report = (error: unknown): void => {
    if (reported) return;
    reported = true;
    try { onError?.(error); } catch { /* a reporter must not break a read */ }
  };
  let wipeReported = false;
  const reportWipeFailure = (error: unknown): void => {
    if (wipeReported) return;
    wipeReported = true;
    try { (opts.onWipeFailure ?? onError)?.(error); } catch { /* a reporter must not break sign-out */ }
  };
  const ownKeys = async (): Promise<string[]> => (await backend.keys()).filter((k) => typeof k === 'string' && k.startsWith(SERVER_DOCS_COPY_KEY_PREFIX));
  const deleteOnce = async (pick: (k: string) => boolean): Promise<void> => {
    const all = await ownKeys();
    const failures: unknown[] = [];
    await Promise.all(all.filter(pick).map((k) => backend.del(k).catch((e: unknown) => { failures.push(e); })));
    if (failures.length > 0) throw failures[0];
  };
  /** Tries twice. A wipe that still fails (a dropped IndexedDB connection) leaves customer data behind, so this store stops reading and writing and says so. Never throws. */
  const deleteWhere = async (pick: (k: string) => boolean): Promise<void> => {
    try {
      await deleteOnce(pick);
    } catch {
      try {
        await deleteOnce(pick);
      } catch (error) {
        wipeFailed = true;
        reportWipeFailure(error);
      }
    }
  };

  return {
    async load(key) {
      if (!isPersisted(key) || disposed || wipeFailed) return undefined;
      const uid = getUid();
      if (!uid) return undefined;
      const k = entryKey(uid, key);
      let entry: unknown;
      try { entry = await backend.get(k); } catch { return undefined; }
      if (getUid() !== uid || disposed || wipeFailed) return undefined; // the user changed (or the store was stopped) while the read was in flight
      if (entry === undefined) return undefined;
      const e = entry as Partial<StoredEntry> | null;
      const at = now();
      const sound = !!e && e.v === 1 && e.uid === uid && e.shape === shape && typeof e.savedAt === 'number'
        && e.savedAt >= at - SERVER_DOCS_COPY_MAX_AGE_MS && isCopy(e.copy);
      if (!sound) {
        await backend.del(k).catch(() => undefined); // expired / other build / malformed: do not keep customer data that will never be used
        return undefined;
      }
      // A copy that was just loaded is not worth rewriting after every delta merge: the write throttle counts from when it was last written.
      lastWrite.set(k, Math.min(e.savedAt as number, at));
      return e.copy;
    },
    async save(key, copyToKeep) {
      if (!isPersisted(key) || storageGivenUp || disposed || wipeFailed) return;
      const uid = getUid();
      if (!uid) return;
      const k = entryKey(uid, key);
      const at = now();
      const last = lastWrite.get(k);
      if (last !== undefined && at - last < SERVER_DOCS_COPY_WRITE_EVERY_MS) return;
      lastWrite.set(k, at);
      try {
        await backend.set(k, { v: 1, uid, shape, savedAt: at, copy: copyToKeep } satisfies StoredEntry);
      } catch (error) {
        if (isQuotaError(error)) storageGivenUp = true;
        else lastWrite.delete(k); // another error must not block the next attempt for 10 minutes
        report(error);
        return;
      }
      if (getUid() !== uid || disposed || wipeFailed) { // the write outlived a sign-out / user switch / demotion: take it back
        lastWrite.delete(k);
        await backend.del(k).catch(() => undefined);
      }
    },
    async clear(key) {
      if (!isPersisted(key)) return;
      const uid = getUid();
      if (!uid) return;
      const k = entryKey(uid, key);
      lastWrite.delete(k);
      await backend.del(k).catch(() => undefined);
    },
    dispose: () => { disposed = true; },
    wipeAll: () => { lastWrite.clear(); return deleteWhere(() => true); },
    wipeOthers: (uid) => deleteWhere((k) => !k.startsWith(`${SERVER_DOCS_COPY_KEY_PREFIX}${uid}:`)),
  };
}
